/* eslint-disable react-refresh/only-export-components */
import { FC, useState, useEffect, createContext, useContext, useCallback } from 'react'
import { LayoutSplashScreen } from '../../../../_theme/layout/core'
import { AuthModel } from './_models'
import * as authHelper from './AuthHelpers'
import { WithChildren } from '../../../../_theme/helpers'
import { getProfileAPI } from './_request'
import {
  PERMISSIONS_STALE_EVENT,
  PermissionsPayload,
  RightFlag,
  getStoredPermissions,
  hasRight,
  stageRightKey,
  storePermissions,
} from './permissions'

type LogoutOptions = {
  skipReturnUrl?: boolean
}

type AuthContextProps = {
  loading: boolean;
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  logout: (options?: LogoutOptions) => void
  skipLoginReturnUrl: boolean
  clearSkipLoginReturnUrl: () => void
  permissions: PermissionsPayload | undefined
  refreshPermissions: () => Promise<void>
}

const initAuthContextPropsState = {
  loading: false,
  auth: {} as any,
  saveAuth: () => { },
  logout: () => { },
  skipLoginReturnUrl: false,
  clearSkipLoginReturnUrl: () => { },
  permissions: undefined,
  refreshPermissions: async () => { },
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [skipLoginReturnUrl, setSkipLoginReturnUrl] = useState(false)
  const [permissions, setPermissions] = useState<PermissionsPayload | undefined>(getStoredPermissions())

  const savePermissions = useCallback((next: PermissionsPayload | undefined) => {
    setPermissions(next)
    storePermissions(next)
  }, [])

  const saveAuth = (auth: AuthModel | undefined) => {
    if (auth) {
      const { permissions: loginPermissions, ...authData } = auth
      setAuth(authData)
      setSkipLoginReturnUrl(false)
      authHelper.setAuth(authData)
      if (loginPermissions) {
        savePermissions(loginPermissions)
      }
    } else {
      setAuth(undefined)
      authHelper.removeAuth()
      savePermissions(undefined)
    }
  }

  const refreshPermissions = useCallback(async () => {
    try {
      const response = await getProfileAPI()
      if (response.status === 'ok' && response.data?.permissions) {
        savePermissions(response.data.permissions)
      }
    } catch (error) {
      console.error('PERMISSIONS REFRESH ERROR', error)
    }
  }, [savePermissions])

  useEffect(() => {
    const onStale = () => { refreshPermissions() }
    window.addEventListener(PERMISSIONS_STALE_EVENT, onStale)
    return () => window.removeEventListener(PERMISSIONS_STALE_EVENT, onStale)
  }, [refreshPermissions])

  const logout = (options?: LogoutOptions) => {
    if (options?.skipReturnUrl) {
      setSkipLoginReturnUrl(true)
    }
    saveAuth(undefined)
  }

  const clearSkipLoginReturnUrl = useCallback(() => {
    setSkipLoginReturnUrl(false)
  }, [])

  return (
    <AuthContext.Provider value={{ loading: false, auth, saveAuth, logout, skipLoginReturnUrl, clearSkipLoginReturnUrl, permissions, refreshPermissions }}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, logout, permissions, refreshPermissions } = useAuth()
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {

    if (auth && auth.user_id) {
      // Refresh rights on every app load so permission changes apply without
      // re-login; block on it only when nothing is cached yet.
      const refreshing = refreshPermissions()
      if (permissions) {
        setShowSplashScreen(false)
      } else {
        refreshing.finally(() => setShowSplashScreen(false))
      }
    } else {
      logout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

/**
 * Permission checks for the current user. Rights the API does not enforce
 * are always granted, so screens can call these unconditionally.
 */
const usePermission = () => {
  const { permissions } = useAuth()

  const can = useCallback(
    (rightKey: string, flag: RightFlag) => hasRight(permissions, rightKey, flag),
    [permissions]
  )
  const canStage = useCallback(
    (dkStage: string | undefined, flag: RightFlag) => hasRight(permissions, stageRightKey(permissions, dkStage), flag),
    [permissions]
  )

  return { can, canStage }
}

/**
 * Returns whether the signed-in user's role lacks View on a right, so links into
 * its module can be disabled or hidden. Signed-out users are not locked out: the
 * login page comes first.
 */
const useIsRightLocked = () => {
  const { auth } = useAuth()
  const { can } = usePermission()

  return useCallback((rightKey: string) => Boolean(auth) && !can(rightKey, 'view'), [auth, can])
}

export { AuthProvider, AuthInit, useAuth, usePermission, useIsRightLocked }
